import { api } from '@/lib/api';
import type {
  IPDBill,
  IPDBillStatus,
  IPDDeposit,
  RunningBalance,
  IPDRefund,
  SettlementSummary,
  IPDChargeMaster,
  IPDChargeableItem,
  IPDInsuranceCase,
  InsuranceClaimView,
  IPDInsurancePolicyRule,
  IPDCreditDebitNote,
  IPDBillAuditLog,
  IPDChargeSourceType,
  PaymentMethod,
  IPDDepositPurpose,
  GSTMaster
} from '@/types/ipdBilling';

const base = '/api/ipd';

export type IPDBillTrack = 'REVENUE' | 'OFFLINE';

/** Query params for parallel offline IPD billing (excluded from revenue reports). */
export function ipdBillTrackParams(billTrack?: IPDBillTrack): { billTrack?: IPDBillTrack } {
  return billTrack === 'OFFLINE' ? { billTrack: 'OFFLINE' } : {};
}

function mergeParams(
  baseParams: Record<string, string | undefined>,
  billTrack?: IPDBillTrack
) {
  return { ...baseParams, ...ipdBillTrackParams(billTrack) };
}

export const ipdBillingApi = {
  // Bill
  getOrCreateBill: (
    admissionId: string,
    billType?: 'CASH' | 'INSURANCE',
    billTrack?: IPDBillTrack
  ) =>
    api.get<{ message: string; data: IPDBill }>(
      `${base}/billing/admission/${admissionId}/bill`,
      { params: mergeParams({ billType: billType ?? 'CASH' }, billTrack) }
    ),
  getBillDetails: (admissionId: string, billTrack?: IPDBillTrack) =>
    api.get<{ message: string; data: IPDBill }>(
      `${base}/billing/admission/${admissionId}/bill-details`,
      { params: ipdBillTrackParams(billTrack) }
    ),
  getBillById: (billId: string) =>
    api.get<{ message: string; data: IPDBill }>(`${base}/billing/ipd-bill/${billId}`),

  // Accrual
  runAccrual: (admissionId: string, billTrack?: IPDBillTrack) =>
    api.post<{ message: string; data: IPDBill }>(
      `${base}/billing/admission/${admissionId}/accrual`,
      undefined,
      { params: ipdBillTrackParams(billTrack) }
    ),

  settlePharmaAtReception: (
    admissionId: string,
    data: { chargeableIds: string[]; paymentMethod: PaymentMethod },
    billTrack?: IPDBillTrack
  ) =>
    api.post<{ message: string; data: unknown }>(
      `${base}/billing/admission/${admissionId}/pharma-settle`,
      data,
      { params: ipdBillTrackParams(billTrack) }
    ),

  // Balance
  getRunningBalance: (admissionId: string, billTrack?: IPDBillTrack) =>
    api.get<{ message: string; data: RunningBalance }>(
      `${base}/billing/admission/${admissionId}/balance`,
      { params: ipdBillTrackParams(billTrack) }
    ),

  // Deposits
  addDeposit: (
    admissionId: string,
    data: {
      amount?: number;
      mode?: PaymentMethod;
      splits?: { mode: PaymentMethod; amount: number }[];
      purpose?: IPDDepositPurpose;
      receiptNumber?: string;
      paymentDate?: string;
      notes?: string;
      amountPendingFromThirdParty?: boolean;
      pendingThirdPartyAmount?: number;
    },
    billTrack?: IPDBillTrack
  ) =>
    api.post<{ message: string; data: IPDDeposit }>(
      `${base}/billing/admission/${admissionId}/deposits`,
      data,
      { params: ipdBillTrackParams(billTrack), timeout: 120_000 }
    ),
  listDeposits: (admissionId: string, billTrack?: IPDBillTrack) =>
    api.get<{ message: string; data: IPDDeposit[] }>(
      `${base}/billing/admission/${admissionId}/deposits`,
      { params: ipdBillTrackParams(billTrack) }
    ),
  updateDeposit: (
    admissionId: string,
    depositId: string,
    data: {
      amount?: number;
      mode?: PaymentMethod;
      purpose?: IPDDepositPurpose;
      receiptNumber?: string | null;
      paymentDate?: string;
      notes?: string | null;
    },
    billTrack?: IPDBillTrack
  ) =>
    api.patch<{ message: string; data: IPDDeposit }>(
      `${base}/billing/admission/${admissionId}/deposits/${depositId}`,
      data,
      { params: ipdBillTrackParams(billTrack) }
    ),
  deleteDeposit: (admissionId: string, depositId: string, billTrack?: IPDBillTrack) =>
    api.delete<{ message: string; data: { id: string } }>(
      `${base}/billing/admission/${admissionId}/deposits/${depositId}`,
      { params: ipdBillTrackParams(billTrack) }
    ),

  // Bill discount (₹ or %)
  updateBillDiscount: (
    billId: string,
    data: { discountPercentage?: number; discountAmount?: number }
  ) =>
    api.patch<{ message: string; data: IPDBill }>(
      `${base}/billing/ipd-bill/${billId}/discount`,
      data
    ),

  applyLoyaltyDiscount: (billId: string, data: { points: number }) =>
    api.post<{
      message: string;
      data: { pointsBurned: number; rupeeValue: number; transactionId: string };
    }>(`${base}/billing/ipd-bill/${billId}/loyalty-discount`, data),

  updateBillDate: (billId: string, billDate: string) =>
    api.patch<{ message: string; data: IPDBill }>(
      `${base}/billing/ipd-bill/${billId}/bill-date`,
      { billDate }
    ),

  // Bill status
  updateBillStatus: (billId: string, data: { status: IPDBillStatus; reason?: string }) =>
    api.patch<{
      message: string;
      data: { bill: IPDBill; settlementSync?: unknown };
    }>(`${base}/billing/ipd-bill/${billId}/status`, data),

  updatePharmacyDisplayMode: (billId: string, data: { mode: 'DETAILED' | 'SUMMARY' | 'HIDDEN_IN_PACKAGE'; reason?: string }) =>
    api.patch<{ message: string; data: IPDBill }>(
      `${base}/billing/ipd-bill/${billId}/pharmacy-display-mode`,
      data
    ),

  /** Insurance/cashless package bills: include pharmacy and/or lab lines in accrual. */
  updatePackageLineInclude: (
    billId: string,
    data:
      | { mode: 'PHARMACY' | 'LAB' | 'BOTH' | 'NONE' }
      | { includePharmacy?: boolean; includeLab?: boolean }
  ) =>
    api.patch<{
      message: string;
      data: { bill: IPDBill; includePackagePharmacy: boolean; includePackageLab: boolean };
    }>(`${base}/billing/ipd-bill/${billId}/package-line-include`, data),

  // Room rate (DRAFT only, ROOM line items)
  updateBillItemRate: (billId: string, itemId: string, unitRate: number) =>
    api.patch<{ message: string; data: IPDBill }>(
      `${base}/billing/ipd-bill/${billId}/items/${itemId}/rate`,
      { unitRate }
    ),
  // Bill line item update (DRAFT only)
  updateBillItem: (
    billId: string,
    itemId: string,
    data: { description?: string; quantity?: number; unitRate?: number; serviceDate?: string }
  ) =>
    api.patch<{ message: string; data: IPDBill }>(
      `${base}/billing/ipd-bill/${billId}/items/${itemId}`,
      data
    ),
  // Bill line item delete (DRAFT only)
  deleteBillItem: (billId: string, itemId: string) =>
    api.delete<{ message: string; data: IPDBill }>(
      `${base}/billing/ipd-bill/${billId}/items/${itemId}`
    ),

  /** Admin hard-delete IPD/ICU final bill document (admission kept). */
  deleteIpdBill: (billId: string, data?: { confirmBillNumber?: string }) =>
    api.delete<{
      message: string;
      data: { billId: string; billNumber: string; admissionId: string };
    }>(`${base}/billing/ipd-bill/${billId}`, {
      data,
      params: data?.confirmBillNumber
        ? { confirmBillNumber: data.confirmBillNumber }
        : undefined,
    }),

  // Refunds & settlement
  addRefund: (
    admissionId: string,
    data: {
      amount: number;
      paymentMode: PaymentMethod;
      reason?: string;
      linkedDepositId?: string;
    },
    billTrack?: IPDBillTrack
  ) =>
    api.post<{ message: string; data: IPDRefund }>(
      `${base}/billing/admission/${admissionId}/refunds`,
      data,
      { params: ipdBillTrackParams(billTrack) }
    ),
  listRefunds: (admissionId: string, billTrack?: IPDBillTrack) =>
    api.get<{ message: string; data: IPDRefund[] }>(
      `${base}/billing/admission/${admissionId}/refunds`,
      { params: ipdBillTrackParams(billTrack) }
    ),
  updateRefund: (
    admissionId: string,
    refundId: string,
    data: { amount?: number; paymentMode?: PaymentMethod; reason?: string },
    billTrack?: IPDBillTrack
  ) =>
    api.patch<{ message: string; data: IPDRefund }>(
      `${base}/billing/admission/${admissionId}/refunds/${refundId}`,
      data,
      { params: ipdBillTrackParams(billTrack) }
    ),
  deleteRefund: (admissionId: string, refundId: string, billTrack?: IPDBillTrack) =>
    api.delete<{ message: string; data: { id: string } }>(
      `${base}/billing/admission/${admissionId}/refunds/${refundId}`,
      { params: ipdBillTrackParams(billTrack) }
    ),
  settleAtDischarge: (admissionId: string, billTrack?: IPDBillTrack) =>
    api.get<{ message: string; data: SettlementSummary }>(
      `${base}/billing/admission/${admissionId}/settle`,
      { params: ipdBillTrackParams(billTrack) }
    ),
  /** Download IPD Final Bill PDF (itemized, enterprise format). */
  getFinalBillPDF: (admissionId: string, billTrack?: IPDBillTrack) =>
    api.get<Blob>(`${base}/billing/admission/${admissionId}/final-bill-pdf`, {
      responseType: 'blob',
      params: ipdBillTrackParams(billTrack),
    }),

  /** Open/print IPD deposit receipt PDF (grouped by receiptNumber). */
  getDepositReceiptPDF: (
    admissionId: string,
    receiptNumber: string,
    billTrack?: IPDBillTrack
  ) =>
    api.get<Blob>(
      `${base}/billing/admission/${admissionId}/deposits/receipt/${encodeURIComponent(receiptNumber)}/pdf`,
      { responseType: 'blob', params: ipdBillTrackParams(billTrack) }
    ),

  // Chargeable items (pharmacy, consumables, misc)
  addChargeableItem: (
    admissionId: string,
    data: {
      sourceType: IPDChargeSourceType;
      chargeCode: string;
      description: string;
      quantity?: number;
      unitRate: number;
      serviceDate?: string;
    }
  ) =>
    api.post<{ message: string; data: IPDChargeableItem }>(
      `${base}/billing/admission/${admissionId}/chargeable-items`,
      data
    ),
  listChargeableItems: (admissionId: string, sourceType?: IPDChargeSourceType) =>
    api.get<{ message: string; data: IPDChargeableItem[] }>(
      `${base}/billing/admission/${admissionId}/chargeable-items`,
      { params: sourceType ? { sourceType } : {} }
    ),
  updateChargeableItem: (
    id: string,
    data: {
      description?: string;
      quantity?: number;
      unitRate?: number;
      serviceDate?: string;
      billSeparately?: boolean;
      pharmacistDiscountPercent?: number;
    }
  ) =>
    api.patch<{ message: string; data: IPDChargeableItem }>(
      `${base}/billing/chargeable-items/${id}`,
      data
    ),
  deleteChargeableItem: (id: string) =>
    api.delete<{ message: string }>(`${base}/billing/chargeable-items/${id}`),

  // Charge master (hospital-level)
  listChargeMasters: (hospitalId: string, sourceType?: IPDChargeSourceType) =>
    api.get<{ message: string; data: IPDChargeMaster[] }>(
      `${base}/billing/charge-master`,
      { params: { hospitalId, ...(sourceType && { sourceType }) } }
    ),
  createChargeMaster: (data: {
    hospitalId: string;
    chargeCode: string;
    description?: string;
    rate: number;
    sourceType: IPDChargeSourceType;
    effectiveFrom?: string;
    effectiveTo?: string;
  }) =>
    api.post<{ message: string; data: IPDChargeMaster }>(
      `${base}/billing/charge-master`,
      data
    ),
  updateChargeMaster: (
    id: string,
    data: { description?: string; rate?: number; effectiveFrom?: string; effectiveTo?: string }
  ) =>
    api.patch<{ message: string; data: IPDChargeMaster }>(
      `${base}/billing/charge-master/${id}`,
      data
    ),

  // Insurance case
  getOrCreateInsuranceCase: (
    admissionId: string,
    data: {
      payerName: string;
      policyNumber: string;
      tpaName?: string;
      coverageType?: string;
      sumInsured?: number;
    },
    billTrack?: IPDBillTrack
  ) =>
    api.post<{ message: string; data: IPDInsuranceCase }>(
      `${base}/billing/admission/${admissionId}/insurance-case`,
      data,
      { params: ipdBillTrackParams(billTrack) }
    ),
  getInsuranceCase: (admissionId: string, billTrack?: IPDBillTrack) =>
    api.get<{ message: string; data: IPDInsuranceCase }>(
      `${base}/billing/admission/${admissionId}/insurance-case`,
      { params: ipdBillTrackParams(billTrack) }
    ),
  updateInsuranceCase: (
    id: string,
    data: Partial<{
      payerName: string;
      policyNumber: string;
      tpaName: string;
      coverageType: string;
      sumInsured: number;
      verifiedAt: string | null;
    }>
  ) =>
    api.patch<{ message: string; data: IPDInsuranceCase }>(
      `${base}/billing/insurance-case/${id}`,
      data
    ),
  verifyInsuranceCase: (id: string) =>
    api.post<{ message: string; data: IPDInsuranceCase }>(
      `${base}/billing/insurance-case/${id}/verify`
    ),

  // Adjudication & claim view
  runAdjudication: (admissionId: string, billTrack?: IPDBillTrack) =>
    api.post<{ message: string; data: InsuranceClaimView }>(
      `${base}/billing/admission/${admissionId}/adjudication`,
      undefined,
      { params: ipdBillTrackParams(billTrack) }
    ),
  getInsuranceClaimView: (admissionId: string, billTrack?: IPDBillTrack) =>
    api.get<{ message: string; data: InsuranceClaimView }>(
      `${base}/billing/admission/${admissionId}/insurance-claim-view`,
      { params: ipdBillTrackParams(billTrack) }
    ),

  // Policy rules (hospital-level)
  listPolicyRules: (hospitalId: string, ruleType?: string) =>
    api.get<{ message: string; data: IPDInsurancePolicyRule[] }>(
      `${base}/billing/policy-rules`,
      { params: { hospitalId, ...(ruleType && { ruleType }) } }
    ),
  createPolicyRule: (data: {
    hospitalId: string;
    ruleType: string;
    ruleValue: number;
    effectiveFrom?: string;
    effectiveTo?: string;
  }) =>
    api.post<{ message: string; data: IPDInsurancePolicyRule }>(
      `${base}/billing/policy-rules`,
      data
    ),
  updatePolicyRule: (
    id: string,
    data: { ruleValue?: number; effectiveFrom?: string; effectiveTo?: string }
  ) =>
    api.patch<{ message: string; data: IPDInsurancePolicyRule }>(
      `${base}/billing/policy-rules/${id}`,
      data
    ),

  // Credit/Debit notes & audit (bill must be FROZEN for notes)
  listCreditDebitNotes: (billId: string) =>
    api.get<{ message: string; data: IPDCreditDebitNote[] }>(
      `${base}/billing/ipd-bill/${billId}/notes`
    ),
  createCreditDebitNote: (
    billId: string,
    data: { noteType: 'CREDIT' | 'DEBIT'; amount: number; reason: string }
  ) =>
    api.post<{ message: string; data: IPDCreditDebitNote }>(
      `${base}/billing/ipd-bill/${billId}/notes`,
      data
    ),
  listBillAuditLog: (billId: string) =>
    api.get<{ message: string; data: IPDBillAuditLog[] }>(
      `${base}/billing/ipd-bill/${billId}/audit-log`
    ),

  // GST Master (admin/superadmin only)
  listGSTMasters: (hospitalId?: string) =>
    api.get<{ message: string; data: GSTMaster[] }>(
      `${base}/billing/gst-master`,
      { params: hospitalId ? { hospitalId } : {} }
    ),
  createGSTMaster: (data: {
    gstCode: string;
    rate: number;
    applicableSourceTypes: string;
    hsnSac?: string;
    hospitalId?: string | null;
    thresholdAmount?: number | null;
    applyToIncremental?: boolean;
    effectiveFrom?: string;
    effectiveTo?: string;
  }) =>
    api.post<{ message: string; data: GSTMaster }>(
      `${base}/billing/gst-master`,
      data
    ),
  updateGSTMaster: (
    id: string,
    data: {
      gstCode?: string;
      rate?: number;
      applicableSourceTypes?: string;
      hsnSac?: string;
      thresholdAmount?: number | null;
      applyToIncremental?: boolean;
      effectiveFrom?: string;
      effectiveTo?: string;
    }
  ) =>
    api.patch<{ message: string; data: GSTMaster }>(
      `${base}/billing/gst-master/${id}`,
      data
    ),
  deleteGSTMaster: (id: string) =>
    api.delete<{ message: string }>(`${base}/billing/gst-master/${id}`),

  listPendingCollections: (billTrack?: IPDBillTrack, status: 'PENDING' | 'COLLECTED' = 'PENDING') =>
    api.get<{ message: string; data: import('@/types/ipdBilling').IPDPendingCollection[] }>(
      `${base}/billing/pending-collections`,
      { params: mergeParams({ status }, billTrack) }
    ),

  listAdmissionPendingCollections: (admissionId: string, billTrack?: IPDBillTrack) =>
    api.get<{ message: string; data: import('@/types/ipdBilling').IPDPendingCollection[] }>(
      `${base}/billing/admission/${admissionId}/pending-collections`,
      { params: ipdBillTrackParams(billTrack) }
    ),

  collectPendingCollection: (
    id: string,
    data: {
      paymentMode: PaymentMethod;
      receiptNumber?: string;
      paymentDate?: string;
      notes?: string;
      settledAmount?: number;
    },
    billTrack?: IPDBillTrack
  ) =>
    api.post<{ message: string; data: import('@/types/ipdBilling').IPDPendingCollection }>(
      `${base}/billing/pending-collections/${id}/collect`,
      data,
      { params: ipdBillTrackParams(billTrack) }
    ),

  /** Delete a wrongly entered PENDING third-party amount (admin / ops manager). */
  deletePendingCollection: (id: string) =>
    api.delete<{ message: string; data: { id: string; admissionId: string } }>(
      `${base}/billing/pending-collections/${id}`
    ),

  /** Revise settled amount on already-collected third-party pending (admin). */
  reviseCollectedSettledAmount: (
    admissionId: string,
    data: { settledAmount: number; notes?: string },
    billTrack?: IPDBillTrack
  ) =>
    api.patch<{
      message: string;
      data: {
        admissionId: string;
        billedTotal: number;
        previousSettledAmount: number;
        settledAmount: number;
        depositId: string | null;
      };
    }>(`${base}/billing/admission/${admissionId}/pending-collections/settled-amount`, data, {
      params: ipdBillTrackParams(billTrack),
    }),

  /** Settle all open third-party pendings for an admission with one final amount. */
  collectAllPendingForAdmission: (
    admissionId: string,
    data: {
      paymentMode: PaymentMethod;
      settledAmount: number;
      receiptNumber?: string;
      paymentDate?: string;
      notes?: string;
    },
    billTrack?: IPDBillTrack
  ) =>
    api.post<{
      message: string;
      data: {
        settledAmount: number;
        billedTotal: number;
        settledCount: number;
        receiptNumber: string;
      };
    }>(`${base}/billing/admission/${admissionId}/pending-collections/collect-all`, data, {
      params: ipdBillTrackParams(billTrack),
    }),
};
